// /notifications — full Notification Log page (iter30).
// Renders a paginated table of all notifications for the current user. Opens
// auto-mark-all-read on mount, supports per-row delete + bulk delete-all-read
// + filter chips (All · Unread · Mentions · SLA · Assignments · Approvals · SRs).
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Trash2, RefreshCw, MailCheck, ChevronLeft, ChevronRight } from "lucide-react";
import {
  useInboxPage,
  useMarkAllInboxRead,
  useDeleteInboxItem,
  useDeleteAllReadInbox,
} from "../api/sitroom";
import { useStore } from "../store";
import { metaFor, relativeTime } from "../data/notifTypeMeta";

const FILTER_CHIPS = [
  { id: "all", label: "All" },
  { id: "unread", label: "Unread" },
  { id: "mention", label: "Mentions" },
  { id: "sla", label: "SLA" },
  { id: "assigned", label: "Assignments" },
  { id: "approval", label: "Approvals" },
  { id: "sr", label: "Service Requests" },
  { id: "email", label: "Emails" },
  { id: "sms", label: "SMS" },
];

const LIMIT = 50;

export default function Notifications() {
  const navigate = useNavigate();
  const openPanel = useStore((s) => s.openPanel);
  const addToast = useStore((s) => s.addToast);

  const [filter, setFilter] = useState("all");
  const [page, setPage] = useState(1);

  const { data, isLoading } = useInboxPage({ filter, page, limit: LIMIT });
  const markAll = useMarkAllInboxRead();
  const deleteOne = useDeleteInboxItem();
  const deleteAllRead = useDeleteAllReadInbox();

  // Auto-mark-all on visiting the page (only on initial mount, not every refetch)
  useEffect(() => {
    markAll.mutate();
  }, []);

  const items = data?.items || [];
  const total = data?.total || 0;
  const hasMore = Boolean(data?.has_more);

  const openSource = (n) => {
    if (n.incident_id) {
      openPanel(n.incident_id);
      navigate(`/incidents?focus=${n.incident_id}`);
    }
  };

  return (
    <div className="min-h-screen p-4 sm:p-8" style={{ background: "var(--bg)" }} data-testid="notifications-page">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
          <div>
            <h1
              data-testid="view-title"
              style={{
                fontFamily: "Syne, sans-serif", fontSize: 26, fontWeight: 700,
                color: "var(--text)", letterSpacing: "-0.4px",
              }}
            >
              Notification Log
            </h1>
            <div
              style={{
                fontFamily: "'DM Mono', monospace", fontSize: 10, color: "#9198b0",
                letterSpacing: "1.5px", textTransform: "uppercase", marginTop: 4,
              }}
            >
              {isLoading ? "Loading…" : `${total} notification${total === 1 ? "" : "s"}`}
            </div>
          </div>
          <button
            type="button"
            onClick={async () => {
              try {
                const res = await deleteAllRead.mutateAsync();
                addToast({ type: "info", title: "Deleted read notifications", body: `${res.deleted} removed` });
              } catch {
                addToast({ type: "info", title: "Delete failed", body: "Try again" });
              }
            }}
            className="flex items-center gap-2 px-3 py-2 rounded-md transition-colors"
            style={{
              background: "rgba(226,75,74,0.08)",
              border: "1px solid rgba(226,75,74,0.45)",
              color: "#ff9a99",
              fontFamily: "'DM Mono', monospace",
              fontSize: 11,
              letterSpacing: "0.5px",
              cursor: "pointer",
            }}
            data-testid="notif-delete-all-read-btn"
          >
            <Trash2 size={12} /> Delete all read
          </button>
        </div>

        {/* Filter bar */}
        <div className="flex flex-wrap gap-2 mb-4" data-testid="notif-filter-bar">
          {FILTER_CHIPS.map((c) => {
            const active = filter === c.id;
            return (
              <button
                key={c.id}
                onClick={() => { setFilter(c.id); setPage(1); }}
                className="px-3 py-1.5 rounded-full transition-colors"
                style={{
                  background: active ? "rgba(24,95,165,0.20)" : "rgba(255,255,255,0.03)",
                  border: `1px solid ${active ? "#185FA5" : "var(--border)"}`,
                  color: active ? "#9ec3ec" : "#c5cad8",
                  fontFamily: "'DM Mono', monospace",
                  fontSize: 11,
                  letterSpacing: "0.5px",
                  cursor: "pointer",
                }}
                data-testid={`notif-filter-${c.id}`}
              >
                {c.label}
              </button>
            );
          })}
        </div>

        {/* Table */}
        <div
          className="rounded-md overflow-hidden"
          style={{ background: "var(--bg2)", border: "1px solid rgba(255,255,255,0.08)" }}
        >
          {isLoading && (
            <div className="p-8 flex items-center justify-center" style={{ color: "#9198b0", fontFamily: "'DM Mono', monospace", fontSize: 12 }}>
              <RefreshCw size={14} className="mr-2" /> Loading…
            </div>
          )}
          {!isLoading && items.length === 0 && (
            <div
              className="p-12 text-center"
              style={{ color: "#9198b0", fontFamily: "'DM Mono', monospace", fontSize: 12, letterSpacing: "0.5px" }}
              data-testid="notif-empty"
            >
              <MailCheck size={24} className="mx-auto mb-3" />
              No notifications for this filter
            </div>
          )}
          {!isLoading && items.length > 0 && (
            <table className="w-full" style={{ borderCollapse: "collapse" }}>
              <thead>
                <tr
                  style={{
                    background: "rgba(255,255,255,0.02)",
                    fontFamily: "'DM Mono', monospace",
                    fontSize: 9,
                    color: "#6b7388",
                    letterSpacing: "1.2px",
                    textTransform: "uppercase",
                  }}
                >
                  <th className="text-left px-3 py-2" style={{ width: 32 }}></th>
                  <th className="text-left px-3 py-2" style={{ width: 36 }}></th>
                  <th className="text-left px-3 py-2">Title / Body</th>
                  <th className="text-left px-3 py-2" style={{ width: 130 }}>Source</th>
                  <th className="text-left px-3 py-2" style={{ width: 100 }}>Received</th>
                  <th className="text-left px-3 py-2" style={{ width: 40 }}></th>
                </tr>
              </thead>
              <tbody>
                {items.map((n) => {
                  const meta = metaFor(n.type);
                  const Icon = meta.Icon;
                  return (
                    <tr
                      key={n.id}
                      style={{
                        borderTop: "1px solid rgba(255,255,255,0.04)",
                        background: n.is_read ? "transparent" : "rgba(24,95,165,0.04)",
                      }}
                      data-testid={`notif-row-${n.id}`}
                    >
                      <td className="px-3 py-2 align-top">
                        {!n.is_read && (
                          <span
                            className="inline-block rounded-full"
                            style={{ width: 8, height: 8, background: "#185FA5", marginTop: 6 }}
                            data-testid="notif-unread-dot"
                            title="Unread"
                          />
                        )}
                      </td>
                      <td className="px-3 py-2 align-top">
                        <Icon size={14} style={{ color: meta.color, marginTop: 4 }} />
                      </td>
                      <td className="px-3 py-2 align-top">
                        <div
                          style={{
                            fontFamily: "Syne, sans-serif",
                            fontWeight: 600,
                            fontSize: 12,
                            color: n.is_read ? "#c5cad8" : "var(--text)",
                          }}
                        >
                          {n.title}
                        </div>
                        {n.body && (
                          <div
                            style={{
                              fontFamily: "'DM Mono', monospace",
                              fontSize: 10,
                              color: "#9198b0",
                              marginTop: 2,
                              lineHeight: 1.5,
                            }}
                          >
                            {n.body}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2 align-top">
                        {n.incident_id ? (
                          <button
                            onClick={() => openSource(n)}
                            style={{
                              background: "transparent",
                              border: "none",
                              color: "#9ec3ec",
                              fontFamily: "'DM Mono', monospace",
                              fontSize: 11,
                              cursor: "pointer",
                              padding: 0,
                              textDecoration: "underline",
                              textDecorationColor: "rgba(158,195,236,0.4)",
                            }}
                            data-testid="notif-source-link"
                          >
                            {n.incident_id}
                          </button>
                        ) : (
                          <span style={{ color: "#6b7388", fontFamily: "'DM Mono', monospace", fontSize: 11 }}>—</span>
                        )}
                      </td>
                      <td
                        className="px-3 py-2 align-top"
                        style={{ color: "#9198b0", fontFamily: "'DM Mono', monospace", fontSize: 10 }}
                        title={n.created_at}
                      >
                        {relativeTime(n.created_at)}
                      </td>
                      <td className="px-3 py-2 align-top">
                        <button
                          onClick={() => deleteOne.mutate(n.id)}
                          aria-label="Delete notification"
                          style={{
                            background: "transparent",
                            border: "none",
                            color: "#9198b0",
                            cursor: "pointer",
                            padding: 2,
                            opacity: 0.6,
                            transition: "opacity 0.15s, color 0.15s",
                          }}
                          onMouseEnter={(e) => { e.currentTarget.style.opacity = "1"; e.currentTarget.style.color = "#E24B4A"; }}
                          onMouseLeave={(e) => { e.currentTarget.style.opacity = "0.6"; e.currentTarget.style.color = "#9198b0"; }}
                          data-testid={`notif-delete-${n.id}`}
                        >
                          <Trash2 size={12} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {!isLoading && total > LIMIT && (
          <div className="flex items-center justify-between mt-4" data-testid="notif-pagination">
            <div
              style={{
                fontFamily: "'DM Mono', monospace",
                fontSize: 10,
                color: "#9198b0",
                letterSpacing: "0.5px",
              }}
            >
              Page {page} of {Math.max(1, Math.ceil(total / LIMIT))}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="flex items-center gap-1 px-3 py-1.5 rounded-md transition-colors"
                style={{
                  background: page <= 1 ? "transparent" : "rgba(255,255,255,0.03)",
                  border: "1px solid rgba(255,255,255,0.10)",
                  color: page <= 1 ? "#5a6080" : "#c5cad8",
                  fontFamily: "'DM Mono', monospace",
                  fontSize: 11,
                  cursor: page <= 1 ? "not-allowed" : "pointer",
                }}
                data-testid="notif-page-prev"
              >
                <ChevronLeft size={12} /> Prev
              </button>
              <button
                onClick={() => setPage((p) => p + 1)}
                disabled={!hasMore}
                className="flex items-center gap-1 px-3 py-1.5 rounded-md transition-colors"
                style={{
                  background: !hasMore ? "transparent" : "rgba(255,255,255,0.03)",
                  border: "1px solid rgba(255,255,255,0.10)",
                  color: !hasMore ? "#5a6080" : "#c5cad8",
                  fontFamily: "'DM Mono', monospace",
                  fontSize: 11,
                  cursor: !hasMore ? "not-allowed" : "pointer",
                }}
                data-testid="notif-page-next"
              >
                Next <ChevronRight size={12} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
